import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import type {InternationlizedPromptSuggestion} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {useLeftRightScrollShadow} from '@Hooks/useLeftRightScrollShadow';
import styles from './ai-text-prompt-box-suggestions.module.scss';

export interface AiTextPromptBoxSuggestionsProps {
  isHidden?: boolean;
  isUnmounted?: boolean;
  numberOfSuggestionsToDisplay?: number;
  onOptionSelect?: (value: string) => void;
  errorMessage?: string;
  suggestions: InternationlizedPromptSuggestion[];
}

export function AiTextPromptBoxSuggestions({numberOfSuggestionsToDisplay = 4, ...props}: AiTextPromptBoxSuggestionsProps): ReactElement<any> | null {
  const isError = !!props.errorMessage;
  const suggestionContainerRef = useRef(null);
  const {leftShadow: shouldShowLeftShadow, rightShadow: shouldShowRightShadow} = useLeftRightScrollShadow(suggestionContainerRef, !!suggestionContainerRef.current);

  const [suggestions, setSuggestions] = useState<InternationlizedPromptSuggestion[]>([]);

  useEffect(() => {
    if (suggestions.length > 0) {
      return;
    }

    const unfilteredSuggestions = [...props.suggestions];
    const shuffledSuggestions = unfilteredSuggestions.sort(randomizeSort).slice(0, numberOfSuggestionsToDisplay).map(getInternationalizedSuggestion);
    setSuggestions(shuffledSuggestions);
  }, [props.suggestions]);

  const getInternationalizedSuggestion = (suggestion: InternationlizedPromptSuggestion): InternationlizedPromptSuggestion => {
    return {
      title: window.i18next.t(suggestion.title),
      subtitle: window.i18next.t(suggestion.subtitle),
    } as InternationlizedPromptSuggestion;
  };

  const randomizeSort = (): number => {
    return 0.5 - Math.random();
  };

  const onSuggestionClick = (title: string, subtitle: string): void => {
    const prompt = `${title} ${subtitle}`;
    props.onOptionSelect?.(prompt.toLowerCase());
  };

  const getSuggestionContainerClassName = (): string => {
    return `
      ${styles.aiPromptBoxSuggestionsContainer} 
      flexbox 
      flex-align-items-end 
      ${isError ? '' : '_full-height'} 
      spacing-p-b-2
      ${props.isHidden ? styles.hidden : ''}
      ${shouldShowRightShadow ? styles.rightShadow : ''}
      ${shouldShowLeftShadow ? styles.leftShadow : ''}
     `;
  };

  if (props.isUnmounted) {
    return null;
  }

  return (
    <div ref={suggestionContainerRef} className={getSuggestionContainerClassName()}>
      {suggestions.map((suggestion) => {
        return (
          <ClickableDiv
            onClick={() => {
              onSuggestionClick(suggestion.title, suggestion.subtitle);
            }}
            className={styles.aiPromptBoxSuggestion}
            key={suggestion.title}
          >
            <div className={styles.suggestionTitle}>{suggestion.title}</div>
            <div className={styles.suggestionSubtitle}>{suggestion.subtitle}</div>
          </ClickableDiv>
        );
      })}
    </div>
  );
}
